import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';

/** the year filter's "All Years" entry - years go on the x-axis and periods are hidden */
export const ALL_YEARS = 'all_years';
/** the period filter's "All Months" / "All Quarters" entry - periods go on the x-axis */
export const ALL_PERIODS = 'all_periods';

/**
 * ST-2614: the most bars a per-site chart draws across the page width and still reads clearly.
 * A chart with more grows wider than the page, its bars keeping that width, and scrolls sideways.
 */
export const MAX_CLEAR_BARS = 36;

/**
 * ST-2614: the font size of a chart widened past the page. Google Charts sizes its text from the
 * chart's width, so a chart several pages wide would draw its labels at twice the usual size.
 */
export const WIDE_CHART_FONT_SIZE = 16;

/**
 * ST-2614: the plot area's margins, in pixels. Google Charts' default margins are a share of the
 * chart's width, so a chart widened to scroll sideways started its plot most of a screen from its
 * left edge; fixed margins start every per-site chart's plot at the same place.
 */
export const PER_SITE_CHART_AREA = { left: 80, right: 30, top: 50, bottom: 60 };

export interface PerSiteRow {
	site_id: string | number;
	site_name: string;
	truck_type: string | null;
	// a row also carries every score and total siteQData produces (about twenty fields,
	// different per device type), and callers pick one by name - hence the open index
	[field: string]: any;
}

/** one x-axis slot: a single period, or a whole year when All Years is selected */
export interface PerSiteBucket {
	key: string;
	label: string;
	rows: PerSiteRow[];
}

/**
 * How a bucket holding several periods collapses to one number per site.
 * Energy totals (Ah, kWh) add up; utilization scores are percentages, so they average.
 */
export type PerSiteAggregate = 'sum' | 'avg';

export interface PerSiteSeriesOptions {
	/** field on the row holding the value to plot, e.g. 'truck_utilization_score' */
	metricField: string;
	/** truck type -> series index, as returned by the backend in trucksIndexes */
	seriesIndexes?: Record<string, number>;
	/** colour palette; the series index picks the colour */
	colors?: string[];
	/** when set, only these site ids are plotted */
	siteIds?: (string | number)[];
	/** human label of the selected period, shown in the tooltip (e.g. 'Q2 2026') */
	periodLabel?: string;
	/** unit appended to the tooltip value (e.g. '%', 'Ah') */
	unit?: string;
	/** decimal places for the tooltip value */
	decimals?: number;
	/**
	 * ST-2614: set when the metric is a score (a ratio) rather than an amount. The bar is then as
	 * tall as the site's own score (its whole-site row) instead of the segments added up, and each
	 * segment takes its part of that height in proportion to its own score: 50% and 50% at a site
	 * scoring 60% are drawn 30 and 30. The tooltip still shows each truck type's own score.
	 */
	scaleToSiteTotal?: boolean;
}

export interface PerSiteChartData {
	// a Google Charts data table is heterogeneous on purpose: a header row mixes strings
	// with {role: ...} objects, and a data row mixes labels, numbers and style strings
	columnNames: any[];
	rows: any[][];
	/** truck type -> colour, for the chart legend */
	legend: { text: string, color: string }[];
	/**
	 * Period and site behind each row, when one x-axis slot stands for a site within a
	 * period. The export needs them as separate columns, and the row itself only holds
	 * the slot's position on the axis.
	 */
	rowMeta?: { periodLabel: string, siteName: string }[];
	/**
	 * ST-2614: x-axis options for a chart whose rows start with a position rather than a label -
	 * one tick per period, centred under its bars. Callers merge it into the chart options.
	 */
	hAxis?: PerSiteChartAxis;
	/** ST-2614: how many bars the chart draws - a stacked bar counts once, whatever its segments */
	barCount?: number;
}

export interface PerSiteChartAxis {
	ticks: { v: number, f: string }[];
	viewWindow: { min: number, max: number };
	gridlines: { color: string };
	minorGridlines: { count: number };
}

/**
 * ST-2448: turns the backend's per-site rows into Google Charts data tables for the
 * Customer Info charts - one bar per site, optionally split into a stacked segment per
 * truck type. Shared so every per-site chart on the page builds its bars the same way.
 */
@Injectable()
export class PerSiteChartsService {

	// `perSite` below is typed `any` throughout: it is the raw backend payload, nested
	// deviceType -> workKey -> year -> period -> rows, with no shared model in the app yet.

	/** default palette, kept in step with the one the period charts have always used */
	readonly defaultColors = [
		'#3366cc', '#dc3912', '#ff9900', '#109618', '#990099', '#0099c6', '#dd4477', '#66aa00', '#b82e2e', '#316395'
	];

	constructor(
		private translateService: TranslateService,
	) { }

	/**
	 * The palette holds ten colours and a customer can have more sites than that.
	 * Wrapping round would hand two sites the same colour and make the legend unreadable,
	 * so past the end we walk the colour wheel in golden-angle steps, which keeps
	 * consecutive generated colours far apart.
	 */
	private colorAt(index: number, colors: string[]): string {
		// a negative series index still wraps inside the palette, the way it always has
		const idx = Math.trunc(index) || 0;
		const safeIndex = idx < 0 ? ((idx % colors.length) + colors.length) % colors.length : idx;

		if (safeIndex < colors.length)
			return colors[safeIndex];

		return `hsl(${Math.round(((safeIndex - colors.length) * 137.508) % 360)}, 65%, 45%)`;
	}

	/** the truck types present, plus the bucket for trucks that carry no type at all */
	private segmentTruckTypes(rows: PerSiteRow[], seriesIndexes?: Record<string, number>, includeUntyped = false): string[] {
		const truckTypes = this.orderTruckTypes(rows.filter(row => row.truck_type != null), seriesIndexes);
		if (includeUntyped)
			truckTypes.push(this.untypedTruckLabel);

		return truckTypes;
	}

	/**
	 * True when some site in scope reports no truck type at all. Checked per site, not
	 * globally: a customer can have one site with typed trucks and another without, and
	 * only the second needs the fallback bucket.
	 */
	private hasUntypedOnlySite(rows: PerSiteRow[]): boolean {
		const typed = new Set<string>();
		const seen = new Set<string>();

		for (const row of rows) {
			// ST-2614: a whole-site row alone does not make a site untyped - the backend drops the
			// truck-type rows of trucks not yet installed, and such a site had no trucks in the period
			if (row.is_site_total === true)
				continue;

			seen.add(String(row.site_id));
			if (row.truck_type != null)
				typed.add(String(row.site_id));
		}

		for (const siteId of seen) {
			if (!typed.has(siteId))
				return true;
		}

		return false;
	}

	/** shown for a site whose trucks have no truck type recorded, so it still gets a bar */
	private get untypedTruckLabel(): string {
		return this.translateService.instant('customer.charts.no_truck_type');
	}

	/**
	 * The rows that make up one site's segments.
	 *
	 * A site whose trucks carry no truck type produces only whole-site rows, and filtering
	 * those out would drop the site from the chart entirely. Such a site gets a single
	 * segment instead, so the bar count still matches the number of sites.
	 */
	private segmentRowsFor(rows: PerSiteRow[], truckType: string, untypedLabel: string): PerSiteRow[] {
		if (truckType === untypedLabel) {
			// Only a site with no truck types at all belongs in this bucket. Every site also
			// carries a whole-site row holding the sum of its segments, so letting a typed site
			// in here would add that sum on top of its own segments and double the bar.
			if (rows.some(row => row.truck_type != null))
				return [];

			// ST-2614: nor does a site holding only its flagged whole-site row - it had no trucks of
			// any type installed in the period, so it gets no "No truck type" segment either
			const hasUntypedGroup = rows.some(row => row.truck_type == null && row.is_site_total === false);
			if (rows.some(row => row.is_site_total === true) && !hasUntypedGroup)
				return [];

			return rows.filter(row => this.isSiteTotalRow(row));
		}

		return rows.filter(row => String(row.truck_type) === truckType);
	}

	/**
	 * ST-2614: the whole-site row of a site. truck_type null also marks a group of trucks that have
	 * no truck type, so the backend flags the whole-site row; a row without the flag falls back to
	 * the old rule.
	 */
	private isSiteTotalRow(row: PerSiteRow): boolean {
		return row.is_site_total ?? row.truck_type == null;
	}

	/**
	 * Picks one slice out of the backend payload.
	 * perSite[deviceTypeKey][workKey][year][periodKey] = [row, ...]
	 */
	getRows(perSite: any, deviceTypeKey: string, workKey: string, year: string, periodKey: string): PerSiteRow[] {
		return perSite?.[deviceTypeKey]?.[workKey]?.[year]?.[periodKey] || [];
	}

	/**
	 * Years to offer, newest first.
	 *
	 * The data alone is not enough: a site whose devices were installed in 2023 but which
	 * never reported has no rows at all, and dropping 2023 from the picker would hide the
	 * fact that it was live. So the list also runs back to the oldest installation date
	 * `perSiteMeta` reports for the sites in scope - those years render as "no data to show"
	 * rather than disappearing.
	 */
	getYears(
		perSite: any,
		deviceTypeKey: string,
		workKey: string,
		options: { meta?: any, siteIds?: (string | number)[] } = {}
	): string[] {
		const years = new Set(Object.keys(perSite?.[deviceTypeKey]?.[workKey] || {}));

		const installedFrom = this.getOldestInstallYear(options.meta, deviceTypeKey, options.siteIds);
		if (installedFrom) {
			const currentYear = +moment().format('YYYY');
			for (let year = installedFrom; year <= currentYear; year++)
				years.add(String(year));
		}

		return Array.from(years).sort((a, b) => +b - +a);
	}

	/** the year picker's items: All Years first, then each year newest to oldest */
	getYearOptions(
		perSite: any,
		deviceTypeKey: string,
		workKey: string,
		options: { meta?: any, siteIds?: (string | number)[] } = {}
	): { id: string, label: string }[] {
		const years = this.getYears(perSite, deviceTypeKey, workKey, options)
			.map(year => ({ id: year, label: year }));

		return [{ id: ALL_YEARS, label: this.translateService.instant('customer.charts.all_years') }, ...years];
	}

	/** the earliest year any in-scope site had a device installed, or 0 when unknown */
	private getOldestInstallYear(meta: any, deviceTypeKey: string, siteIds?: (string | number)[]): number {
		const perSiteMeta = meta?.[deviceTypeKey];
		if (!perSiteMeta)
			return 0;

		const allowed = siteIds?.length ? new Set(siteIds.map(id => String(id))) : null;
		let oldest = 0;

		for (const siteId of Object.keys(perSiteMeta)) {
			if (allowed && !allowed.has(String(siteId)))
				continue;

			const year = +perSiteMeta[siteId]?.installYear;
			if (!Number.isFinite(year) || year <= 0)
				continue;

			if (!oldest || year < oldest)
				oldest = year;
		}

		return oldest;
	}

	/**
	 * Period options for the picker, e.g. [{id: 'Q2', label: 'Q2'}] or [{id: '06', label: 'Jun'}].
	 * Ordered oldest to newest so the dropdown reads naturally.
	 */
	getPeriods(perSite: any, deviceTypeKey: string, workKey: string, year: string): { id: string, label: string }[] {
		const periodKeys = Object.keys(perSite?.[deviceTypeKey]?.[workKey]?.[year] || {});

		return periodKeys
			.sort((a, b) => this.periodSortValue(a) - this.periodSortValue(b))
			.map(id => ({ id, label: this.getPeriodLabel(id) }));
	}

	/**
	 * The period picker's items: All Months / All Quarters first, then each period that
	 * holds data for the selected year. Empty while All Years is selected, since the
	 * period filters are hidden in that mode.
	 */
	getPeriodOptions(perSite: any, deviceTypeKey: string, workKey: string, year: string): { id: string, label: string }[] {
		if (this.isAllYears(year))
			return [];

		const allKey = workKey == 'quarters' ? 'customer.charts.all_quarters' : 'customer.charts.all_months';

		return [
			{ id: ALL_PERIODS, label: this.translateService.instant(allKey) },
			...this.getPeriods(perSite, deviceTypeKey, workKey, year),
		];
	}

	isAllYears(year: string): boolean {
		return year === ALL_YEARS;
	}

	isAllPeriods(period: string): boolean {
		return period === ALL_PERIODS;
	}

	/** true when the x-axis carries periods or years rather than sites */
	isBucketAxis(year: string, period: string): boolean {
		return this.isAllYears(year) || this.isAllPeriods(period);
	}

	/**
	 * The x-axis slots for the current filter selection:
	 * - All Years  -> one bucket per year, holding every period in that year
	 * - All Months -> one bucket per period in the selected year
	 * - a single period -> not a bucket axis; callers use the per-site builders instead
	 */
	getBuckets(perSite: any, deviceTypeKey: string, workKey: string, year: string, period: string): PerSiteBucket[] {
		const byYear = perSite?.[deviceTypeKey]?.[workKey] || {};

		if (this.isAllYears(year)) {
			return Object.keys(byYear)
				.sort((a, b) => +a - +b)
				.map(yearKey => ({
					key: yearKey,
					label: yearKey,
					rows: Object.keys(byYear[yearKey] || {})
						.sort((a, b) => this.periodSortValue(a) - this.periodSortValue(b))
						.reduce((rows, periodKey) => rows.concat(byYear[yearKey][periodKey] || []), [] as PerSiteRow[]),
				}));
		}

		if (!this.isAllPeriods(period))
			return [];

		return this.getPeriods(perSite, deviceTypeKey, workKey, year)
			.map(item => ({
				key: item.id,
				label: item.label,
				rows: this.getRows(perSite, deviceTypeKey, workKey, year, item.id),
			}));
	}

	/** a quarter key stays as it is; a month key becomes a short month name */
	getPeriodLabel(periodKey: string): string {
		if (!periodKey)
			return '';

		if (periodKey.startsWith('Q'))
			return periodKey;

		return moment(periodKey, 'MM').format('MMM');
	}

	/**
	 * Joins the period label and the year, e.g. "Q2 2026".
	 *
	 * The two "All" selections read as themselves: All Years has no single year to name,
	 * and All Months / All Quarters covers the whole of the selected year.
	 */
	getPeriodDisplay(periodKey: string, year: string, workKey = 'months'): string {
		if (this.isAllYears(year))
			return this.translateService.instant('customer.charts.all_years');

		if (this.isAllPeriods(periodKey)) {
			const allKey = workKey == 'quarters' ? 'customer.charts.all_quarters' : 'customer.charts.all_months';
			return [this.translateService.instant(allKey), year].filter(Boolean).join(' ');
		}

		const label = this.getPeriodLabel(periodKey);
		return [label, year].filter(Boolean).join(' ');
	}

	/**
	 * One stacked bar per site, one segment per truck type - the Truck Utilization charts.
	 */
	buildStackedBySiteAndTruckType(rows: PerSiteRow[], options: PerSiteSeriesOptions): PerSiteChartData {
		const colors = options.colors?.length ? options.colors : this.defaultColors;
		const scoped = this.filterRows(rows, options.siteIds);

		// a site whose trucks carry no truck type has only whole-site rows; without a bucket
		// for them it would drop off the chart and the bar count would not match the sites
		const untypedLabel = this.untypedTruckLabel;
		const hasUntyped = this.hasUntypedOnlySite(scoped);
		const segments = hasUntyped ? scoped : scoped.filter(row => row.truck_type != null);

		const truckTypes = this.segmentTruckTypes(scoped, options.seriesIndexes, hasUntyped);
		const colorByTruckType: Record<string, string> = {};
		truckTypes.forEach((truckType, idx) => {
			const seriesIndex = options.seriesIndexes?.[truckType];
			const colorIdx = (seriesIndex != null ? seriesIndex - 1 : idx);
			colorByTruckType[truckType] = this.colorAt(colorIdx, colors);
		});

		const columnNames: any[] = [this.translateService.instant('customer.charts.site')];
		for (const truckType of truckTypes)
			columnNames.push(truckType, { role: 'style' }, { role: 'tooltip', p: { html: true } });

		const bySite = this.groupBySite(segments);
		const siteTotals = this.groupBySite(scoped.filter(row => this.isSiteTotalRow(row)));
		const chartRows: any[][] = [];

		for (const siteKey of Object.keys(bySite)) {
			const siteRows = bySite[siteKey];
			const siteName = siteRows[0].site_name;
			const row: any[] = [siteName];

			// ST-2614: a truck type the site did not have draws nothing - no segment and no tooltip.
			// Its value is undefined and its tooltip '', never null: sct-charts turns every null cell into
			// 0, which still draws a sliver with a "0" / "NaN" tooltip, while Google Charts leaves an
			// undefined cell empty.
			const segmentRows = truckTypes.map(truckType => this.segmentRowsFor(siteRows, truckType, untypedLabel)[0]);
			const perTruckType = segmentRows.map(segmentRow => segmentRow ? this.readMetric(segmentRow, options.metricField) : undefined);
			if (perTruckType.every(value => value == null))
				continue;

			const siteTotal = options.scaleToSiteTotal
				? this.readMetric(siteTotals[siteKey]?.[0], options.metricField)
				: perTruckType.reduce((sum, value) => sum + (value ?? 0), 0);
			const heights = options.scaleToSiteTotal ? this.scaleToTotal(perTruckType, siteTotal) : perTruckType;

			truckTypes.forEach((truckType, idx) => {
				row.push(
					heights[idx],
					colorByTruckType[truckType],
					perTruckType[idx] == null ? '' : this.buildTooltip(siteName, perTruckType[idx], options, truckType, siteTotal)
				);
			});

			chartRows.push(row);
		}

		const legend = truckTypes.map(truckType => ({ text: truckType, color: colorByTruckType[truckType] }));
		return { columnNames, rows: chartRows, legend, barCount: chartRows.length };
	}

	/**
	 * One stacked bar per site, per period - the truck charts under All Months, All
	 * Quarters and All Years.
	 *
	 * The story asks for two things at once: the x-axis carries the periods, and every
	 * site still gets its own bar split into truck-type segments. A column chart can only
	 * group or stack on one categorical axis, not both, so each x-axis slot is one site
	 * within one period and the truck types stay the stacked series.
	 * That draws exactly what was asked: within June, one bar per site, each segmented.
	 *
	 * ST-2614: the slots sit on a number axis - period i spans i to i + 1, its sites side by
	 * side with one empty slot as the gap to the next period - and `hAxis` labels each period
	 * once, under the middle of its bars. On a category axis Google Charts showed only every
	 * Nth slot's label, and with five sites a period that left just "Jan, Apr, Jul, Oct".
	 */
	buildStackedBySiteAcrossBuckets(
		buckets: PerSiteBucket[],
		options: PerSiteSeriesOptions & { aggregate?: PerSiteAggregate }
	): PerSiteChartData {
		const colors = options.colors?.length ? options.colors : this.defaultColors;
		const aggregate = options.aggregate || 'sum';

		// Segments only, or the whole-site rows would be counted on top of their own parts.
		// The exception is a site whose trucks carry no truck type: it has nothing but
		// whole-site rows, so it gets a single bucket rather than dropping off the chart.
		const scopedRows = buckets.map(bucket => this.filterRows(bucket.rows, options.siteIds));
		const untypedLabel = this.untypedTruckLabel;
		const hasUntyped = this.hasUntypedOnlySite([].concat(...scopedRows));
		const bucketRows = scopedRows.map(rows => hasUntyped ? rows : rows.filter(row => row.truck_type != null));

		const truckTypes = this.segmentTruckTypes([].concat(...scopedRows), options.seriesIndexes, hasUntyped);
		const colorByTruckType: Record<string, string> = {};
		truckTypes.forEach((truckType, idx) => {
			const seriesIndex = options.seriesIndexes?.[truckType];
			const colorIdx = (seriesIndex != null ? seriesIndex - 1 : idx);
			colorByTruckType[truckType] = this.colorAt(colorIdx, colors);
		});

		const columnNames: any[] = [this.translateService.instant('customer.charts.period')];
		for (const truckType of truckTypes)
			columnNames.push(truckType, { role: 'style' }, { role: 'tooltip', p: { html: true } });

		const chartRows: any[][] = [];
		const rowMeta: { periodLabel: string, siteName: string }[] = [];
		const ticks: { v: number, f: string }[] = [];

		buckets.forEach((bucket, bucketIdx) => {
			const bySite = this.groupBySite(bucketRows[bucketIdx]);
			const siteTotals = this.groupBySite(scopedRows[bucketIdx].filter(row => this.isSiteTotalRow(row)));
			// a site keeps its slot even when it draws nothing, so the two score charts, built from
			// the same rows, put their bars and ticks in the same places
			const siteKeys = Object.keys(bySite);
			const slotWidth = 1 / (siteKeys.length + 1);
			let drawnInBucket = 0;

			siteKeys.forEach((siteKey, siteIdx) => {
				const siteRows = bySite[siteKey];
				const siteName = siteRows[0].site_name;
				// ST-2614: a truck type the site did not have in the bucket is undefined with a '' tooltip,
				// not a 0 segment (see buildStackedBySiteAndTruckType for why not null)
				const segmentRows = truckTypes.map(truckType => this.segmentRowsFor(siteRows, truckType, untypedLabel));
				const perTruckType = segmentRows.map(rows => rows.length ? this.aggregateRows(rows, options.metricField, aggregate) : undefined);
				if (perTruckType.every(value => value == null))
					return;

				const siteTotal = options.scaleToSiteTotal
					? this.aggregateRows(siteTotals[siteKey] || [], options.metricField, aggregate)
					: perTruckType.reduce((sum, value) => sum + (value ?? 0), 0);
				const heights = options.scaleToSiteTotal ? this.scaleToTotal(perTruckType, siteTotal) : perTruckType;

				// the bar's place on the axis; the period's name is its tick, and the tooltip tells the
				// site bars apart
				const row: any[] = [bucketIdx + (siteIdx + 0.5) * slotWidth];
				drawnInBucket++;
				truckTypes.forEach((truckType, idx) => {
					row.push(
						heights[idx],
						colorByTruckType[truckType],
						perTruckType[idx] == null
							? ''
							: this.buildTooltip(siteName, perTruckType[idx], { ...options, periodLabel: bucket.label }, truckType, siteTotal)
					);
				});

				chartRows.push(row);
				rowMeta.push({ periodLabel: bucket.label, siteName });
			});

			// a period with no bars gets no label either, as before
			if (drawnInBucket)
				ticks.push({ v: bucketIdx + (siteKeys.length / 2) * slotWidth, f: bucket.label });
		});

		const legend = truckTypes.map(truckType => ({ text: truckType, color: colorByTruckType[truckType] }));
		const hAxis: PerSiteChartAxis = {
			ticks,
			viewWindow: { min: 0, max: buckets.length },
			// the positions are not values to read off the axis, so no grid lines between them
			gridlines: { color: 'transparent' },
			minorGridlines: { count: 0 },
		};

		return { columnNames, rows: chartRows, legend, rowMeta, hAxis, barCount: chartRows.length };
	}

	/**
	 * One solid bar per site - the Charger, Battery and Usage Data charts. Reads the
	 * whole-site row so a site with several truck types still gets a single value rather
	 * than a colour-segmented bar.
	 */
	buildSingleBarBySite(rows: PerSiteRow[], options: PerSiteSeriesOptions & { seriesLabel?: string }): PerSiteChartData {
		const colors = options.colors?.length ? options.colors : this.defaultColors;
		const color = colors[0];
		const siteRows = this.filterRows(rows, options.siteIds).filter(row => row.truck_type == null);

		const columnNames: any[] = [
			this.translateService.instant('customer.charts.site'),
			options.seriesLabel || '',
			{ role: 'style' },
			{ role: 'tooltip', p: { html: true } }
		];

		const bySite = this.groupBySite(siteRows);
		const chartRows: any[][] = [];

		for (const siteKey of Object.keys(bySite)) {
			const row = bySite[siteKey][0];
			const value = this.readMetric(row, options.metricField);

			chartRows.push([
				row.site_name,
				value,
				color,
				this.buildTooltip(row.site_name, value, options)
			]);
		}

		return { columnNames, rows: chartRows, legend: [], barCount: chartRows.length };
	}

	/**
	 * One column per site across a period or year x-axis - the All Months, All Quarters and
	 * All Years views.
	 *
	 * Sites become the series here, so a truck chart shows a site's total rather than a
	 * stack of truck types: stacking by truck type and grouping by site cannot share one
	 * axis. The truck type breakdown stays in the single-period view, and the Truck Type
	 * filter still decides what feeds each total.
	 */
	buildSitesAcrossBuckets(
		buckets: PerSiteBucket[],
		options: PerSiteSeriesOptions & { aggregate?: PerSiteAggregate }
	): PerSiteChartData {
		const colors = options.colors?.length ? options.colors : this.defaultColors;
		const aggregate = options.aggregate || 'sum';

		// site totals only; the per-truck-type rows would double-count against them
		const bucketRows = buckets.map(bucket => this.filterRows(bucket.rows, options.siteIds).filter(row => row.truck_type == null));

		const siteNames: Record<string, string> = {};
		for (const rows of bucketRows) {
			for (const row of rows)
				siteNames[String(row.site_id)] = row.site_name;
		}

		const siteIds = Object.keys(siteNames).sort((a, b) => String(siteNames[a]).localeCompare(String(siteNames[b])));

		const colorBySite: Record<string, string> = {};
		siteIds.forEach((siteId, idx) => colorBySite[siteId] = this.colorAt(idx, colors));

		const columnNames: any[] = [this.translateService.instant('customer.charts.period')];

		// ST-2547: the buckets come from every site, the columns only from the selected ones. With
		// no selected site holding rows there is no series to draw, and period rows alone make
		// Google Charts throw "Not enough columns" - so return no rows and let the empty state show.
		if (!siteIds.length)
			return { columnNames, rows: [], legend: [], barCount: 0 };

		for (const siteId of siteIds)
			columnNames.push(siteNames[siteId], { role: 'style' }, { role: 'tooltip', p: { html: true } });

		const chartRows: any[][] = [];

		buckets.forEach((bucket, bucketIdx) => {
			const row: any[] = [bucket.label];
			const bySite = this.groupBySite(bucketRows[bucketIdx]);

			for (const siteId of siteIds) {
				const value = this.aggregateRows(bySite[siteId] || [], options.metricField, aggregate);

				row.push(
					value,
					colorBySite[siteId],
					this.buildTooltip(siteNames[siteId], value, { ...options, periodLabel: bucket.label })
				);
			}

			chartRows.push(row);
		});

		const legend = siteIds.map(siteId => ({ text: siteNames[siteId], color: colorBySite[siteId] }));
		// every period row holds one bar per site
		return { columnNames, rows: chartRows, legend, barCount: chartRows.length * siteIds.length };
	}

	/**
	 * Collapses however many period rows a bucket holds into one number.
	 *
	 * `avg` is an approximation for utilization scores: the backend only ever produces a
	 * score per period, so a yearly figure has to come from the monthly ones, and months
	 * are not all the same length. It is close enough to compare sites, which is what these
	 * charts are for, and it beats summing percentages into a meaningless total.
	 */
	private aggregateRows(rows: PerSiteRow[], metricField: string, aggregate: PerSiteAggregate): number {
		if (!rows.length)
			return 0;

		const total = rows.reduce((sum, row) => sum + this.readMetric(row, metricField), 0);
		return aggregate == 'avg' ? total / rows.length : total;
	}

	/**
	 * ST-2614: how wide to draw a chart, in % of its container. Up to MAX_CLEAR_BARS bars fill the
	 * page as they always have; with more, the chart grows so each bar keeps the width it has at
	 * MAX_CLEAR_BARS, and its container scrolls sideways.
	 */
	getChartWidthPercent(chart: PerSiteChartData): number {
		const barCount = chart.barCount ?? chart.rows.length;
		return Math.max(100, Math.ceil(barCount / MAX_CLEAR_BARS * 100));
	}

	/**
	 * ST-2614: chart options for the width a chart is drawn at. Every chart gets the same fixed
	 * margins; a widened chart also keeps the usual font size, where a chart that fits the page
	 * keeps Google Charts' own.
	 */
	getChartWidthOptions(widthPercent: number): { chartArea: typeof PER_SITE_CHART_AREA, fontSize?: number } {
		return {
			chartArea: { ...PER_SITE_CHART_AREA },
			...widthPercent > 100 ? { fontSize: WIDE_CHART_FONT_SIZE } : {},
		};
	}

	/**
	 * ST-2614: shares a site's score out between its segments in proportion to their own scores,
	 * so they stack up to that score exactly. A truck type the site did not have stays empty.
	 */
	private scaleToTotal(values: (number | undefined)[], total: number): (number | undefined)[] {
		const sum = values.reduce((acc, value) => acc + (value ?? 0), 0);
		return values.map(value => value == null ? undefined : (sum ? value / sum * total : 0));
	}

	/**
	 * Flattens a built chart into the CSV layout the three Usage Data charts share:
	 * one row per site per period, with a column per truck type where the chart has them.
	 *
	 * Reads back the chart that was drawn rather than re-querying, so the file and the
	 * screen cannot disagree - and so one selected month collapses to one row per site
	 * while All Months gives each site a row per month, from the same code.
	 */
	buildExportTable(
		chart: PerSiteChartData,
		options: { periodLabel: string, bucketAxis: boolean, breakdown?: boolean, siteLabel?: string, decimals?: number }
	): { headers: string[], rows: any[][] } {
		// ST-2595: the file shows the numbers the way the tooltip does, not the raw bar values
		// ST-2614: a truck type a site did not have is empty on the chart - a blank cell here, not 0
		const round = (value: unknown) => value == null ? '' : this.roundForDisplay(value, options.decimals);
		const seriesNames: string[] = [];
		for (let idx = 1; idx < chart.columnNames.length; idx += 3)
			seriesNames.push(String(chart.columnNames[idx]));

		const periodHeader = this.translateService.instant('customer.charts.period');
		const siteHeader = options.siteLabel || this.translateService.instant('customer.charts.site');
		const rows: any[][] = [];

		// the truck charts fold period and site into one x-axis label and keep truck types as
		// the series; rowMeta carries the two apart so they can be their own columns here
		if (chart.rowMeta?.length) {
			const headers = [periodHeader, siteHeader, ...seriesNames, this.translateService.instant('g.total')];

			chart.rows.forEach((chartRow, rowIdx) => {
				const values = seriesNames.map((_, seriesIdx) => chartRow[1 + seriesIdx * 3]);
				const total = values.reduce((sum, value) => sum + (typeof value == 'number' ? value : 0), 0);
				const meta = chart.rowMeta[rowIdx];

				rows.push([meta.periodLabel, meta.siteName, ...values.map(round), round(total)]);
			});

			return { headers, rows };
		}

		if (options.bucketAxis) {
			// x-axis is the period, each series is a site: unpick it back into site rows
			const headers = [periodHeader, siteHeader, ...seriesNames.length ? [this.translateService.instant('g.value')] : []];

			for (const chartRow of chart.rows) {
				seriesNames.forEach((siteName, seriesIdx) => {
					rows.push([chartRow[0], siteName, round(chartRow[1 + seriesIdx * 3])]);
				});
			}

			return { headers, rows };
		}

		// x-axis is the site: the series are truck types (or a single value column).
		// The caller decides, so a customer with one truck type still gets the same columns
		// as one with three and the two files can be compared.
		const isBreakdown = options.breakdown ?? seriesNames.length > 1;
		const headers = [
			periodHeader,
			siteHeader,
			...seriesNames,
			...isBreakdown ? [this.translateService.instant('g.total')] : [],
		];

		for (const chartRow of chart.rows) {
			const values = seriesNames.map((_, seriesIdx) => chartRow[1 + seriesIdx * 3]);
			const total = values.reduce((sum, value) => sum + (typeof value == 'number' ? value : 0), 0);

			rows.push([options.periodLabel, chartRow[0], ...values.map(round), ...isBreakdown ? [round(total)] : []]);
		}

		return { headers, rows };
	}

	/**
	 * ST-2595: a value rounded the way the tooltip shows it (buildTooltip: toFixed(decimals)), for
	 * exports, so a file matches the screen. Anything that is not a number is returned as it is.
	 */
	roundForDisplay<T>(value: T, decimals = 0): T | number {
		return typeof value === 'number' && Number.isFinite(value) ? Number(value.toFixed(decimals)) : value;
	}

	private filterRows(rows: PerSiteRow[], siteIds?: (string | number)[]): PerSiteRow[] {
		if (!rows?.length)
			return [];

		if (!siteIds?.length)
			return rows;

		const allowed = new Set(siteIds.map(id => String(id)));
		return rows.filter(row => allowed.has(String(row.site_id)));
	}

	/** the backend already sorts by site name; grouping keeps that order */
	private groupBySite(rows: PerSiteRow[]): Record<string, PerSiteRow[]> {
		const bySite: Record<string, PerSiteRow[]> = {};

		for (const row of rows) {
			const key = String(row.site_id);
			bySite[key] ||= [];
			bySite[key].push(row);
		}

		return bySite;
	}

	/** series order follows trucksIndexes so a truck type keeps its colour across charts */
	private orderTruckTypes(rows: PerSiteRow[], seriesIndexes?: Record<string, number>): string[] {
		const present = new Set(rows.map(row => String(row.truck_type)));

		if (seriesIndexes && Object.keys(seriesIndexes).length) {
			const ordered = Object.keys(seriesIndexes)
				.filter(truckType => present.has(truckType))
				.sort((a, b) => seriesIndexes[a] - seriesIndexes[b]);

			// a truck type in the data but missing from trucksIndexes still deserves a segment
			for (const truckType of present) {
				if (!ordered.includes(truckType))
					ordered.push(truckType);
			}

			return ordered;
		}

		return Array.from(present).sort();
	}

	readMetric(row: PerSiteRow | undefined, metricField: string): number {
		const value = row?.[metricField];
		return typeof value === 'number' && Number.isFinite(value) ? value : 0;
	}

	private buildTooltip(
		siteName: string,
		value: number,
		options: PerSiteSeriesOptions,
		truckType?: string,
		siteTotal?: number
	): string {
		const decimals = options.decimals ?? 0;
		const unit = options.unit ? ` ${options.unit}` : '';

		const lines = [`<b>${siteName}</b>`];
		if (truckType)
			lines.push(truckType);

		if (options.periodLabel)
			lines.push(options.periodLabel);

		lines.push(`<b>${value.toFixed(decimals)}${unit}</b>`);

		// a segment on its own says nothing about how big the site's bar is
		if (siteTotal != null)
			lines.push(`${this.translateService.instant('g.total')}: ${siteTotal.toFixed(decimals)}${unit}`);

		return `<div class="p-2 font-size-12">${lines.join('<br>')}</div>`;
	}

	private periodSortValue(periodKey: string): number {
		if (periodKey?.startsWith('Q'))
			return +periodKey.replace('Q', '');

		return +periodKey;
	}
}
